/**
 * TabSystem - Custom web component that handles tab functionality
 * Usage:
 * <x-tabbed-content>
 *   <div class="tab-list" role="tablist">
 *     <button role="tab" aria-controls="panel-1" aria-selected="true">Tab 1</button>
 *     <button role="tab" aria-controls="panel-2" aria-selected="false">Tab 2</button>
 *   </div>
 *   <div id="panel-1" role="tabpanel">Panel 1 content</div>
 *   <div id="panel-2" role="tabpanel" hidden>Panel 2 content</div>
 * </x-tabbed-content>
 */
export class TabbedContent extends HTMLElement {
  private tabList: HTMLElement | null = null
  private tabs: HTMLElement[] = []
  private tabFocus: number = 0

  constructor() {
    super()
    this.changeTabs = this.changeTabs.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  connectedCallback(): void {
    this.tabList = this.querySelector('[role="tablist"]')

    if (!this.tabList) {
      console.error('TabSystem requires a child element with role="tablist"')
      return
    }

    this.tabs = Array.from(this.tabList.querySelectorAll('[role="tab"]')) as HTMLElement[]

    if (this.tabs.length === 0) {
      console.error('TabSystem requires at least one tab element with role="tab"')
      return
    }

    this.tabFocus = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    if (this.tabFocus === -1) this.tabFocus = 0

    this.tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === this.tabFocus ? '0' : '-1')
      tab.addEventListener('click', this.changeTabs)
    })
    this.tabList.addEventListener('keydown', this.handleKeydown)
  }

  disconnectedCallback(): void {
    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this.changeTabs)
    })

    if (this.tabList) {
      this.tabList.removeEventListener('keydown', this.handleKeydown)
    }
  }

  changeTabs(e: Event): void {
    const targetTab = e.currentTarget as HTMLElement
    if (!this.tabs.includes(targetTab)) return
    e.stopPropagation()

    this.tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', 'false')
      tab.setAttribute('tabindex', '-1')
    })
    targetTab.setAttribute('aria-selected', 'true')
    targetTab.setAttribute('tabindex', '0')
    this.tabFocus = this.tabs.indexOf(targetTab)

    this.tabs.forEach((tab) => {
      const panelId = tab.getAttribute('aria-controls')
      if (!panelId) return
      const panel = this.querySelector(`#${CSS.escape(panelId)}`)
      if (!panel) return
      if (tab === targetTab) {
        panel.removeAttribute('hidden')
      } else {
        panel.setAttribute('hidden', '')
      }
    })
  }

  handleKeydown(e: KeyboardEvent): void {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()

    let newTabFocus = this.tabFocus
    if (e.key === 'ArrowRight') {
      newTabFocus = newTabFocus >= this.tabs.length - 1 ? 0 : newTabFocus + 1
    } else {
      newTabFocus = newTabFocus <= 0 ? this.tabs.length - 1 : newTabFocus - 1
    }

    this.tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === newTabFocus ? '0' : '-1')
    })
    this.tabs[newTabFocus].focus()
    this.tabFocus = newTabFocus
  }
}
